// src/pages/AdminPage/SettingsTab.tsx
// Вкладка «Настройки»: лимит размера файлов сценария и чата (контекстные документы).
import { useEffect, useState } from 'react';
import { Box, Typography, Button, TextField, CircularProgress, Skeleton } from '@mui/material';
import { HardDrive, Check, AlertCircle } from 'lucide-react';
import {
  getInstanceSettings,
  updateContextDocumentMaxSize,
  UPLOAD_MAX_SIZE_MIN_MB,
  UPLOAD_MAX_SIZE_MAX_MB,
} from 'services/settingsApi';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: 13,
    '& fieldset': { borderColor: 'var(--color-border)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--color-primary)' },
  },
  '& .MuiInputLabel-root': { fontSize: 13 },
};

/** Возвращает текст ошибки валидации либо null, если значение корректно */
function validateSizeMb(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return 'Укажите размер';
  if (!/^\d+$/.test(trimmed)) return 'Только целое число';
  const value = Number(trimmed);
  if (value < UPLOAD_MAX_SIZE_MIN_MB || value > UPLOAD_MAX_SIZE_MAX_MB) {
    return `Допустимо от ${UPLOAD_MAX_SIZE_MIN_MB} до ${UPLOAD_MAX_SIZE_MAX_MB} МБ`;
  }
  return null;
}

export default function SettingsTab() {
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [initialValue, setInitialValue] = useState<number | null>(null);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getInstanceSettings()
      .then((settings) => {
        if (!active) return;
        const mb = settings?.upload_max_size?.context_document_parsing;
        if (typeof mb === 'number') {
          setInitialValue(mb);
          setValue(String(mb));
        } else {
          setLoadError(true);
        }
      })
      .catch(() => active && setLoadError(true))
      .finally(() => active && setIsLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const validationError = validateSizeMb(value);
  const parsed = validationError ? null : Number(value.trim());
  const isDirty = parsed !== null && parsed !== initialValue;
  const canSave = !saving && isDirty && validationError === null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    setSaved(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (parsed === null) return;
    setSaving(true);
    setSaved(false);
    setSaveError(null);
    try {
      const updated = await updateContextDocumentMaxSize(parsed);
      const mb = updated?.upload_max_size?.context_document_parsing ?? parsed;
      setInitialValue(mb);
      setValue(String(mb));
      setSaved(true);
    } catch {
      setSaveError('Не удалось сохранить настройку. Проверьте подключение и попробуйте ещё раз.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      {/* Инфо-баннер */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.5,
          mb: 2.5,
          bgcolor: 'rgba(20,116,255,0.06)',
          border: '1px solid rgba(20,116,255,0.14)',
          borderRadius: 'var(--radius-md)',
        }}
      >
        <HardDrive size={18} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
        <Typography sx={{ fontSize: 12.5, color: 'var(--color-text-secondary)' }}>
          Ограничивает максимальный размер файлов, загружаемых в сценарии и чат (контекстные
          документы). Значение указывается в мегабайтах и применяется ко всему инстансу.
        </Typography>
      </Box>

      {/* Карточка настройки */}
      <Box
        sx={{
          background: '#FFFFFF',
          border: '1px solid rgba(20,116,255,0.10)',
          boxShadow: '0 2px 16px rgba(20,116,255,0.08), 0 1px 4px rgba(0,0,0,0.03)',
          borderRadius: 'var(--radius-lg)',
          p: 2.5,
          maxWidth: 480,
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 750, mb: 0.5 }}>
          Размер файлов сценария и чата
        </Typography>
        <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', mb: 2 }}>
          Максимальный размер одного загружаемого документа.
        </Typography>

        {isLoading ? (
          <Skeleton height={56} variant="rounded" />
        ) : loadError ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#ef4444' }}>
            <AlertCircle size={16} />
            <Typography sx={{ fontSize: 12.5 }}>Не удалось загрузить настройки.</Typography>
          </Box>
        ) : (
          <>
            <TextField
              label="Максимальный размер (МБ)"
              value={value}
              onChange={handleChange}
              error={value.trim() !== '' && validationError !== null}
              helperText={value.trim() !== '' ? (validationError ?? ' ') : ' '}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              sx={{ ...fieldSx, width: 220 }}
              size="small"
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
              <Button
                variant="contained"
                disabled={!canSave}
                onClick={() => void handleSave()}
                startIcon={
                  saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : undefined
                }
                sx={{
                  bgcolor: 'var(--color-primary)',
                  fontSize: 13,
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 'var(--radius-md)',
                  boxShadow: 'none',
                  px: 2,
                  '&:hover': { bgcolor: '#4a4de0', boxShadow: 'none' },
                }}
              >
                Сохранить
              </Button>

              {saved && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#059669' }}>
                  <Check size={15} />
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>Сохранено</Typography>
                </Box>
              )}
              {saveError && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#ef4444' }}>
                  <AlertCircle size={15} />
                  <Typography sx={{ fontSize: 12 }}>{saveError}</Typography>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
